import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GatewayModuleType, GatewayUpdateLatestVersionModel, GatewayUpdateProcessType } from 'core/domain/gateways/model';
import { GetGatewayLastVersionByGatewayId } from 'core/domain/gateways/repositories/getGatewayLastVersionByGatewayId';
import { useUserSession } from 'hooks/useUserSession';
import { useEnvironment } from 'hooks/useEnvironment';
import { GetGatewayDetail } from 'core/domain/gateways/repositories/getGatewayDetail';
import { GatewayDetailStatusModel, GatewayParsed } from 'core/domain/gateways/model/gateway/gatewayParsed';
import { GatewayStatusModel } from 'core/domain/gateways/model/gatewayStatus';
import { Thresholds } from 'core/domain/gateways/model/thresholds';
import { checkIfVersionIsOutdated } from 'utils/checkVersions';
import { UserRoleType } from 'models/users.model';
import { CheckGatewayModuleVersionUpdateState } from 'core/domain/gateways/repositories/checkGatewayModuleVersionUpdateState';
import { ActiveIssue } from 'core/domain/gateways/model/activeIssue';
import { CHECK_UPDATE_GATEWAY_MODULE_TIME_INTERVAL } from 'constants/time';
import { UpdateGatewayModuleVersion } from 'core/domain/gateways/repositories/updateGatewayModuleVersion';
import {
  GatewayNetworkInterfaceModel,
  GatewayNetworkStateStatusModel,
} from 'core/domain/gateways/model/gatewayStatus/gatewayStatusPayload';
import { useMessage } from 'hooks/useMessage';
import { useRebootGateway } from 'hooks/useRebootGateway';
import UserProfileContext, { UserProfileContextState } from 'context/UserProfileContext';
import {
  CheckValuesManager,
  CheckValuesType,
  CustomGatewayDataModel,
  GatewayStatusCheckValueModel,
  GatewayStatusInformationTranslations,
  GatewayStatusKeys,
  GatewayStatusModuleConfig,
  GatewayStatusPercentValueInfoModel,
  GatewayStatusPercentValueModel,
  PercentValuesType,
} from 'components/pages/AssetPage/Tabs/TabGateway/resources/utils';

export const useGatewayDetailInfo = () => {
  const { t } = useTranslation();
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const { token } = useUserSession();
  const { host } = useEnvironment();
  const { setMessageError } = useMessage();
  const { userProfile } = useContext<UserProfileContextState>(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [gateway, setGateway] = useState<CustomGatewayDataModel>();
  const {
    rebootGatewayLiterals,
    onRebootGateway,
    onCancelRebootGateway,
    onConfirmRebootGateway,
    isRebootGatewayLoading,
    isRebootGatewayModalVisible,
    isConfirmRebootGatewayButtonDisabled,
  } = useRebootGateway();

  const isUserRoleSuperAdmin = userProfile?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN);

  const lastSystemVersion: string = gateway?.systemConfig.lastVersion || '';

  const initialModulesVersions: GatewayUpdateLatestVersionModel = { aass: '0.0.0', system: '0.0.0' };

  const gatewayStatusInformationTranslations: GatewayStatusInformationTranslations = {
    title: t('information'),
    notReportedText: t('not_reported'),
    productionDateText: t('date_of_production'),
    networkStateText: t('_GATEWAY_STATUS_DETAIL_NETWORK_STATE'),
    networkStateStatusTranslations: {
      [GatewayNetworkStateStatusModel.GOOD]: t('_GATEWAY_STATUS_NETWORK_STATE_STATUS_GOOD'),
      [GatewayNetworkStateStatusModel.MEDIUM]: t('_GATEWAY_STATUS_NETWORK_STATE_STATUS_REGULAR'),
      [GatewayNetworkStateStatusModel.BAD]: t('_GATEWAY_STATUS_NETWORK_STATE_STATUS_BAD'),
      [GatewayNetworkStateStatusModel.UNKNOWN]: t('not_reported'),
    },
    networkStateLatencyText: t('_GATEWAY_STATUS_NETWORK_STATE_LATENCY'),
    networkStateBandWidthText: t('_GATEWAY_STATUS_NETWORK_STATE_BAND_WIDTH'),
    networkStateLostPackagesText: t('_GATEWAY_STATUS_NETWORK_STATE_LOST_PACKAGES'),
    networkInterfaceText: t('_GATEWAY_STATUS_DETAIL_CONNECTION_TYPE'),
    networkInterfaceDetailTranslations: {
      [GatewayNetworkInterfaceModel.ETHERNET]: t('_GATEWAY_STATUS_CONNECTION_TYPE_ETHERNET'),
      [GatewayNetworkInterfaceModel.WIFI]: t('_GATEWAY_STATUS_CONNECTION_TYPE_WIFI'),
      [GatewayNetworkInterfaceModel.LTE]: t('_GATEWAY_STATUS_CONNECTION_TYPE_LTE'),
    },
    lastConnectionText: t('last_connection'),
    statusText: t('status'),
    statusOfflineText: t('offline'),
    statusOnlineText: t('online'),
    healthText: t('health'),
    activeIssuesText: t('active_issues'),
    activeIssuesDetailTranslations: {
      [ActiveIssue.ASSISTANT_CLOUD_SERVICE]: t('ACTIVE_ISSUES.assistant_cloud_service'),
      [ActiveIssue.CPU_HDD_USED]: t('ACTIVE_ISSUES.cpu_hdd_used'),
      [ActiveIssue.CPU_HD_AVAILABLE]: t('ACTIVE_ISSUES.cpu_hd_available'),
      [ActiveIssue.CPU_HD_USED]: t('ACTIVE_ISSUES.cpu_hd_used'),
      [ActiveIssue.CPU_LOAD]: t('ACTIVE_ISSUES.cpu_load'),
      [ActiveIssue.CPU_RAM]: t('ACTIVE_ISSUES.cpu_ram'),
      [ActiveIssue.CPU_RAM_AVAILABLE]: t('ACTIVE_ISSUES.cpu_ram_available'),
      [ActiveIssue.CPU_RAM_USED]: t('ACTIVE_ISSUES.cpu_ram_used'),
      [ActiveIssue.CPU_TEMPERATURE]: t('ACTIVE_ISSUES.cpu_temperature'),
      [ActiveIssue.OHS_API]: t('ACTIVE_ISSUES.ohs_api'),
      [ActiveIssue.OHS_SERVICE]: t('ACTIVE_ISSUES.ohs_service'),
      [ActiveIssue.ONLINE]: t('ACTIVE_ISSUES.online'),
      [ActiveIssue.OPENHAB_API]: t('ACTIVE_ISSUES.openhab_api'),
      [ActiveIssue.OPENHAB_SERVICED]: t('ACTIVE_ISSUES.openhab_serviced'),
      [ActiveIssue.SYSTEM_TIME]: t('ACTIVE_ISSUES.system_time'),
      [ActiveIssue.TOTAL_INODES]: t('ACTIVE_ISSUES.total_inodes'),
      [ActiveIssue.TOTAL_SWAP]: t('ACTIVE_ISSUES.total_swap'),
      [ActiveIssue.UPTIME]: t('ACTIVE_ISSUES.uptime'),
      [ActiveIssue.USED_INODES]: t('ACTIVE_ISSUES.used_inodes'),
      [ActiveIssue.USED_SWAP]: t('ACTIVE_ISSUES.used_swap'),
      [ActiveIssue.VPN_CONNECTION]: t('ACTIVE_ISSUES.vpn_connection'),
      [ActiveIssue.VPN_IP]: t('ACTIVE_ISSUES.vpn_ip'),
      [ActiveIssue.ZWAVE_CONTROLLER]: t('ACTIVE_ISSUES.zwave_controller'),
    },
    gatewayVersionText: t('gw_version'),
    gatewayVersionUpdateButtonText: t('_GATEWAY_UPDATE_BUTTON_TEXT', { version: lastSystemVersion }),
    updateModuleErrorMessage: t('_GATEWAY_UPDATE_MODULE_VERSION_ERROR'),
    updateButtonInProgressText: t('updating'),
  };

  const getModulesVersion = async (gatewayId: string): Promise<GatewayUpdateLatestVersionModel> => {
    try {
      return await GetGatewayLastVersionByGatewayId({ host, token, gatewayId });
    } catch (error) {
      return initialModulesVersions;
    }
  };

  const checkValuesManager: CheckValuesManager = {
    [CheckValuesType.ZWAVE_CONTROLLER]: (element: boolean): GatewayStatusCheckValueModel => ({
      check: element,
      text: t(CheckValuesType.ZWAVE_CONTROLLER),
    }),
    [CheckValuesType.HELPER_API]: (element: boolean): GatewayStatusCheckValueModel => ({
      check: element,
      text: t(CheckValuesType.HELPER_API),
    }),
    [CheckValuesType.VPN_CONNECTION]: (element: boolean): GatewayStatusCheckValueModel => ({
      check: element,
      text: t(CheckValuesType.VPN_CONNECTION),
    }),
    [CheckValuesType.ASSISTANT_CLOUD_SERVICE]: (element: boolean): GatewayStatusCheckValueModel => ({
      check: element,
      text: t(CheckValuesType.ASSISTANT_CLOUD_SERVICE),
    }),
  };

  const getGatewayStatusCheckedValues = (gateway: GatewayStatusModel) => {
    const gatewayKeys: GatewayStatusKeys[] = Object.keys(gateway) as GatewayStatusKeys[];
    const filteredGatewayKeys = gatewayKeys.filter((key) => Object.values(CheckValuesType).includes(key as CheckValuesType));
    const checkedValues: GatewayStatusCheckValueModel[] = [];
    filteredGatewayKeys.forEach((gatewayKey) => {
      if (typeof gateway[gatewayKey] === 'boolean') {
        const checkedFunction = checkValuesManager[gatewayKey as CheckValuesType];
        checkedValues.push(checkedFunction(gateway[gatewayKey] as boolean));
      }
    });
    return checkedValues;
  };

  const percentValuesManager = {
    [PercentValuesType.CPU_TEMPERATURE]: ({
      value,
      threshold,
      units,
    }: GatewayStatusPercentValueInfoModel): GatewayStatusPercentValueModel => ({
      value,
      threshold,
      unit: 'ºC',
      text: t(PercentValuesType.CPU_TEMPERATURE),
    }),
    [PercentValuesType.CPU_RAM_USED]: ({
      value,
      threshold,
      units,
    }: GatewayStatusPercentValueInfoModel): GatewayStatusPercentValueModel => ({
      value,
      threshold,
      unit: '%',
      text: t(PercentValuesType.CPU_RAM_USED),
    }),
    [PercentValuesType.CPU_HDD_USED]: ({
      value,
      threshold,
      units,
    }: GatewayStatusPercentValueInfoModel): GatewayStatusPercentValueModel => ({
      value,
      threshold,
      unit: '%',
      text: t(PercentValuesType.CPU_HDD_USED),
    }),
    [PercentValuesType.CPU_LOAD]: ({ value, threshold, units }: GatewayStatusPercentValueInfoModel): GatewayStatusPercentValueModel => ({
      value,
      threshold,
      unit: '',
      text: t(PercentValuesType.CPU_LOAD),
    }),
  };

  const getGatewayStatusPercentValues = (gateway: GatewayDetailStatusModel) => {
    const gatewayKeys: GatewayStatusKeys[] = Object.keys(gateway) as GatewayStatusKeys[];
    const filteredGatewayKeys = gatewayKeys.filter((key) => Object.values(PercentValuesType).includes(key as PercentValuesType));
    const checkedValues: GatewayStatusPercentValueModel[] = [];
    filteredGatewayKeys.forEach((gatewayKey) => {
      if (typeof gateway[gatewayKey] === 'number') {
        const checkedFunction = percentValuesManager[gatewayKey as PercentValuesType];
        checkedValues.push(
          checkedFunction({
            value: gateway[gatewayKey] as number,
            threshold: gateway.thresholds[gatewayKey as keyof Thresholds] as number,
            units: gateway.units,
          })
        );
      }
    });
    return checkedValues;
  };

  const payloadToCustomGatewayStatusData = ({
    gatewayDetail,
    versions,
  }: {
    gatewayDetail: GatewayParsed;
    versions: GatewayUpdateLatestVersionModel;
  }): CustomGatewayDataModel => {
    const { aass, system } = versions;
    const { daemonVersion, gatewayVersion } = gatewayDetail;
    const checkedValues = !!gatewayDetail.status ? getGatewayStatusCheckedValues(gatewayDetail.status) : [];
    const percentValues = !!gatewayDetail.status ? getGatewayStatusPercentValues(gatewayDetail.status) : [];
    const isAassUpdateEnabled = checkIfVersionIsOutdated({ minimumVersion: aass, version: daemonVersion });
    const isSystemUpdateEnabled = checkIfVersionIsOutdated({ minimumVersion: system, version: gatewayVersion });

    return {
      ...gatewayDetail,
      assetId: '',
      checkedValues,
      percentValues,
      aassLastVersion: aass,
      systemConfig: {
        isVersionUpdateError: false,
        isVersionUpdating: false,
        isUpdateButtonDisabled: false,
        isUpdateButtonVisible: (!!isUserRoleSuperAdmin && isSystemUpdateEnabled) || (!!isUserRoleSuperAdmin && isAassUpdateEnabled),
        lastVersion: system,
      },
    };
  };

  const checkGatewayDetailUpdateState = async (gatewayId: string): Promise<GatewayUpdateProcessType> => {
    try {
      const { state } = await CheckGatewayModuleVersionUpdateState({
        host,
        token,
        gatewayId,
        module: GatewayModuleType.SYSTEM,
      });
      return state;
    } catch (error) {
      setMessageError({ description: t('_GATEWAY_STATUS_DETAIL_CHECK_UPDATE_STATE_ERROR') });
      return GatewayUpdateProcessType.FAILED;
    }
  };

  const updateFailedModuleConfig = (module: GatewayStatusModuleConfig): GatewayStatusModuleConfig => {
    const isGatewayModuleUpdatedToLastVersion = !module.isUpdateButtonVisible;
    const isError = !!isUserRoleSuperAdmin && !isGatewayModuleUpdatedToLastVersion;
    return { ...module, isVersionUpdateError: isError };
  };

  const moduleVersionUpdateInitialStateManager = {
    [GatewayUpdateProcessType.COMPLETED]: (module: GatewayStatusModuleConfig) => ({ ...module }),
    [GatewayUpdateProcessType.IN_PROGRESS]: (module: GatewayStatusModuleConfig) => ({
      ...module,
      isVersionUpdating: true,
      isUpdateButtonVisible: true,
    }),
    [GatewayUpdateProcessType.FAILED]: updateFailedModuleConfig,
  };

  const checkGatewayUpdateState = async (gateway: CustomGatewayDataModel) => {
    try {
      const gatewayUpdateState = !!isUserRoleSuperAdmin
        ? await checkGatewayDetailUpdateState(gateway.id)
        : GatewayUpdateProcessType.COMPLETED;
      const systemModule: GatewayStatusModuleConfig = moduleVersionUpdateInitialStateManager[gatewayUpdateState](gateway.systemConfig);
      setGateway({ ...gateway, systemConfig: systemModule });
    } catch (error) {
      console.warn(error);
    }
  };

  const getGatewayData = async () => {
    setLoading(true);
    try {
      const gatewayDetail = await GetGatewayDetail(gatewayId);
      const versions = !!isUserRoleSuperAdmin ? await getModulesVersion(gatewayId) : initialModulesVersions;
      const parsedGateway = payloadToCustomGatewayStatusData({ gatewayDetail, versions });
      setGateway(parsedGateway);
      checkGatewayUpdateState(parsedGateway);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  };

  const checkUpdateStateInProgress = (gateway: CustomGatewayDataModel) => {
    const systemConfig: GatewayStatusModuleConfig = { ...gateway.systemConfig, isVersionUpdating: true, isVersionUpdateError: false };
    setGateway({ ...gateway, systemConfig });
  };

  const checkUpdateStatus = async (gateway: CustomGatewayDataModel) => {
    try {
      const response = await CheckGatewayModuleVersionUpdateState({ host, token, gatewayId: gateway.id, module: GatewayModuleType.SYSTEM });
      response.state === GatewayUpdateProcessType.COMPLETED ? getGatewayData() : checkUpdateStateInProgress(gateway);
    } catch (error) {
      const systemConfig: GatewayStatusModuleConfig = {
        ...gateway.systemConfig,
        isVersionUpdating: false,
        isVersionUpdateError: true,
      };
      setGateway({ ...gateway, systemConfig });
    }
  };

  const onUpdateGateway = async (gateway: CustomGatewayDataModel) => {
    try {
      checkUpdateStateInProgress(gateway);
      await UpdateGatewayModuleVersion({ host, token, gatewayId: gateway.id, module: GatewayModuleType.SYSTEM });
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    getGatewayData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (!!gateway?.systemConfig.isVersionUpdating) {
      intervalId = setInterval(() => {
        checkUpdateStatus(gateway);
      }, CHECK_UPDATE_GATEWAY_MODULE_TIME_INTERVAL);
    }
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway]);

  const rebootOptions = {
    rebootGatewayLiterals,
    onRebootGateway,
    onCancelRebootGateway,
    onConfirmRebootGateway,
    isRebootGatewayLoading,
    isRebootGatewayModalVisible,
    isConfirmRebootGatewayButtonDisabled,
    hasPermissionToRebootGateway: isUserRoleSuperAdmin ?? false,
  };

  return {
    gateway,
    translations: gatewayStatusInformationTranslations,
    rebootOptions,
    onUpdateGateway,
    loading,
  };
};
